import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Header } from '@/ui/components/Header';
import { Footer } from '@/ui/components/Footer';
import { MobilePageHeader } from '@/ui/components/MobilePageHeader';
import { SEO } from '@/ui/components/SEO';
import { Scale, User, Server, Copyright, Handshake, Network } from 'lucide-react';

/**
 * Page Mentions Légales — /mentions-legales
 * Obligations légales françaises (loi pour la confiance dans l'économie numérique).
 */
const LegalMentionsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    const sections = [
        {
            icon: <User className="w-5 h-5" />,
            titleKey: 'legal.page.editor.title',
            items: [
                'legal.page.editor.name',
                'legal.page.editor.status',
                'legal.page.editor.address',
                'legal.page.editor.identification',
                'legal.page.editor.vat',
                'legal.page.editor.contact',
            ],
        },
        {
            icon: <Server className="w-5 h-5" />,
            titleKey: 'legal.page.hosting.title',
            items: [
                'legal.page.hosting.provider',
                'legal.page.hosting.address',
            ],
        },
        {
            icon: <Copyright className="w-5 h-5" />,
            titleKey: 'legal.page.intellectual.title',
            items: [
                'legal.page.intellectual.ownership',
                'legal.page.intellectual.images',
                'legal.page.intellectual.data',
                'legal.page.intellectual.lightPollution',
            ],
        },
        {
            icon: <Network className="w-5 h-5" />,
            titleKey: 'legal.page.services.title',
            items: [
                'legal.page.services.intro',
                'legal.page.services.stripe',
                'legal.page.services.google',
                'legal.page.services.openmeteo',
                'legal.page.services.metno',
                'legal.page.services.brevo',
                'legal.page.services.sentry',
                'legal.page.services.firebase',
            ],
        },
        {
            icon: <Handshake className="w-5 h-5" />,
            titleKey: 'legal.page.partners.title',
            items: [
                'legal.page.partners.astrobin',
            ],
        },
    ];

    return (
        <>
            {/* Pas de clés seo.* dédiées : on réutilise le titre de la page et les intitulés
                de ses sections, déjà traduits dans les 6 langues. */}
            <SEO
                title={t('legal.page.title')}
                description={`${t('legal.page.title')} — ${[t('legal.page.editor.title'), t('legal.page.hosting.title'), t('legal.page.intellectual.title')].join(', ')}.`}
            />
            {isNative
                ? <MobilePageHeader title={t('legal.page.title')} icon="gavel" showBackButton onBack={() => navigate(-1)} />
                : <Header />
            }
            <main className={`min-h-screen bg-[#0f2023] text-white ${isNative ? 'pt-20 pb-24' : 'pt-28 pb-20'}`}>
                <div className="max-w-3xl mx-auto px-6">
                    {/* Titre */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm mb-4">
                            <Scale className="w-4 h-4" />
                            {t('legal.page.badge')}
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-3">
                            {t('legal.page.title')}
                        </h1>
                        <p className="text-slate-400 text-sm">
                            {t('legal.page.lastUpdate')}
                        </p>
                    </div>

                    {/* Sections */}
                    <div className="space-y-8">
                        {sections.map((section, idx) => (
                            <section
                                key={idx}
                                className="bg-white/[0.03] border border-white/10 rounded-xl p-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-primary">{section.icon}</div>
                                    <h2 className="text-lg font-semibold">
                                        {t(section.titleKey)}
                                    </h2>
                                </div>
                                <ul className="space-y-2">
                                    {section.items.map((itemKey, i) => (
                                        <li
                                            key={i}
                                            className="text-slate-400 text-sm leading-relaxed flex items-start gap-2"
                                        >
                                            <span className="text-primary/60 mt-1.5">•</span>
                                            <span>{t(itemKey)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                </div>
            </main>
            {!isNative && <Footer />}
        </>
    );
};

export default LegalMentionsPage;

